import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

import type { DealEventFormInterface } from '@zaoblako/shared/modules/business/dto/deal-event/deal.event.form.dto';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import type { ChecklistFormInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-form.dto';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import moment from 'moment-timezone';

@Component({
  selector: 'crm-checklist-period-form',
  standalone: true,
  imports: [CommonModule, DatepickerComponent, JoinPipe],
  templateUrl: './checklist-period-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChecklistPeriodFormComponent implements OnInit, OnChanges {
  @Input() public startAt: Date;
  @Input() public endAt: Date;

  @Output() public startAtChange = new EventEmitter<Date>();
  @Output() public endAtChange = new EventEmitter<Date>();

  @Input() public errors: ValidationErrorInterface<ChecklistFormInterface | DealEventFormInterface>;
  @Input() public appendTo: string;

  @Input() public canDelete = false;

  @Input() public minDateStart: Date;
  @Input() public minDateEnd: Date;

  @Input() public isDisabledStart = false;
  @Input() public isDisabledEnd = false;
  @Input() public isExtendTime = false;

  public oldStartDate: Date;
  public oldEndDate: Date;

  public ngOnInit() {
    const momentNow = moment();
    const minuteArray = [0, 15, 30, 45];
    for (let i = 0; i !== 15; i++) {
      const index = minuteArray.findIndex((min) => momentNow.minutes() + i === min || momentNow.minutes() - i === min);
      if (index !== -1) {
        if (this.canDelete) {
          this.minDateStart = momentNow.set({ hour: 0, minute: 0, second: 0 }).toDate();
        } else if (this.isExtendTime) {
          this.minDateStart = momentNow.toDate();
          this.endAt = moment(this.minDateStart).add(15, 'minutes').toDate();
        } else {
          this.minDateStart = momentNow.set({ minute: minuteArray[index], second: 0 }).toDate();
        }
        this.minDateEnd = moment(this.minDateStart).add(15, 'minutes').toDate();
        if (!this.canDelete && !this.isExtendTime) {
          this.startAt = this.minDateStart;
          this.endAt = this.minDateEnd;
          this.startAtChange.emit(this.startAt);
          this.endAtChange.emit(this.endAt);
        }
        break;
      }
    }
    this.oldStartDate = this.startAt;
    this.oldEndDate = this.endAt;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.startAt?.currentValue || changes.endAt?.currentValue) {
      this.fixDateStartAndEnd();
      this.oldStartDate = this.startAt;
      this.oldEndDate = this.endAt;
    }
  }

  public changeDateStartAt() {
    let startAt = this.startAt;
    const differenceMinute = moment(startAt).diff(moment(this.oldStartDate), 'minutes');
    if (differenceMinute === -45) {
      startAt = moment(startAt).add(1, 'hour').toDate();
    } else if (differenceMinute === 45) {
      startAt = moment(startAt).subtract(1, 'hour').toDate();
    }

    if (moment(startAt).isBefore(moment(this.minDateStart))) {
      if (moment().startOf('day').diff(moment(startAt).startOf('day'), 'day')) {
        startAt = this.oldStartDate;
      } else {
        startAt = this.minDateStart;
      }
    }

    this.minDateEnd = moment(startAt).add(15, 'minutes').toDate();
    if (moment(this.endAt).isBefore(moment(this.minDateEnd)) || moment(this.endAt).isAfter(moment(this.minDateEnd))) {
      this.endAt = this.minDateEnd;
    }

    setTimeout(() => {
      this.startAt = startAt;
      this.startAtChange.emit(this.startAt);
      this.fixDateStartAndEnd();
      this.endAtChange.emit(this.endAt);
      this.oldStartDate = this.startAt;
    }, 0);
  }

  public changeDateEndAt() {
    let endAt = this.endAt;
    const differenceMinute = moment(endAt).diff(moment(this.oldEndDate), 'minutes');
    if (differenceMinute === -45) {
      endAt = moment(endAt).add(1, 'hour').toDate();
    } else if (differenceMinute === 45) {
      endAt = moment(endAt).subtract(1, 'hour').toDate();
    }

    if (moment(endAt).isBefore(moment(this.minDateEnd))) {
      endAt = this.oldEndDate;
    }

    setTimeout(() => {
      this.endAt = endAt;
      this.fixDateStartAndEnd();
      this.endAtChange.emit(this.endAt);
      this.oldEndDate = this.endAt;
    }, 0);
  }

  private fixDateStartAndEnd() {
    if (moment(this.startAt).startOf('day').diff(moment(this.endAt).startOf('day'), 'day') && this.startAt) {
      const startDate = moment(this.startAt);
      this.endAt = moment(this.endAt)
        .set({ year: startDate.year(), month: startDate.month(), day: startDate.day() })
        .toDate();
    }
  }
}
